import React from "react";
import { useSearchParams } from "react-router";

import { useGo } from "@refinedev/core";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { DataTableActionBar } from "@/components/refine-ui/data-table/data-table-action-bar";
import { ListView } from "@/components/refine-ui/views/list-view";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { EventGroupsTable } from "@/pages/event-groups/components/event-groups-table";
import { RowPreview } from "@/pages/events/components/row-preview";
import { DeleteCancelledModal } from "@/pages/events/features/delete-cancelled/delete-cancelled-modal";
import { useEventsTable } from "@/pages/events/hooks/use-events-table";

/**
 * `/events?view=groups` lists event groups in place of events. The two views are separate
 * components so the events table's hook (which fetches events) does not run behind the groups.
 * Only `view=groups` selects the groups; any other value is the events table.
 */
export const EventsList = () => {
  const [searchParams] = useSearchParams();
  return searchParams.get("view") === "groups" ? (
    <EventGroupsView />
  ) : (
    <EventsTableView />
  );
};

/**
 * Writes `view=groups` into the url, or clears `view` to leave the groups. `keepQuery` carries the
 * events table's own filters, sort and search through both ways, so turning the switch off brings
 * back the table the curator left. A cleared key is dropped by the router binding, not left empty.
 */
const GroupsOnlySwitch = ({ checked }: { checked: boolean }) => {
  const go = useGo();
  return (
    <div className="flex items-center space-x-2">
      <Switch
        id="groups-only"
        checked={checked}
        onCheckedChange={(on) =>
          go({
            query: { view: on ? "groups" : undefined },
            options: { keepQuery: true },
            type: "replace",
          })
        }
      />
      <Label
        htmlFor="groups-only"
        className="flex flex-row gap-1 items-center whitespace-nowrap"
      >
        Groups only
      </Label>
    </div>
  );
};

const NOT_FOR_GROUPS = "Not available for groups";

/**
 * The events bar's switches, in the events bar's order, for the groups view: the ones that only
 * mean something for events stay in place, disabled, and the Groups only switch does not move.
 */
const EventGroupsView = () => (
  <ListView>
    <EventGroupsTable
      controls={
        <>
          <div className="flex items-center space-x-2" title={NOT_FOR_GROUPS}>
            <Checkbox id="ticketing" disabled />
            <label
              htmlFor="ticketing"
              className="text-sm font-medium leading-none whitespace-nowrap peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Ticketing
            </label>
          </div>
          <div className="flex items-center space-x-2" title={NOT_FOR_GROUPS}>
            <Checkbox id="placeholder-art" disabled />
            <label
              htmlFor="placeholder-art"
              className="text-sm font-medium leading-none whitespace-nowrap peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Placeholder Art
            </label>
          </div>
          <Switch
            id="show-previews"
            checked={false}
            disabled
            title={NOT_FOR_GROUPS}
          />
          <Label
            htmlFor="show-previews"
            className="flex flex-row gap-1 items-center whitespace-nowrap"
            title={NOT_FOR_GROUPS}
          >
            Show Previews
          </Label>
          <GroupsOnlySwitch checked />
        </>
      }
    />
  </ListView>
);

const EventsTableView = () => {
  const { table, hovering, globalFilters } = useEventsTable();
  const [showPreviews, setShowPreviews] = React.useState(false);

  React.useEffect(() => {
    const savedState = localStorage.getItem("events-show-previews");
    if (savedState) setShowPreviews(JSON.parse(savedState));
  }, []);

  return (
    <ListView>
      <DataTableActionBar
        resource="events"
        table={table}
        menuItems={<DeleteCancelledModal />}
      >
        <div className="flex items-center space-x-2">
          <Checkbox
            id="ticketing"
            defaultChecked={globalFilters.ticketing.isApplied}
            onCheckedChange={globalFilters.ticketing.toggle}
          />
          <label
            htmlFor="terms2"
            className="text-sm font-medium leading-none whitespace-nowrap peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Ticketing
          </label>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="placeholder-art"
            defaultChecked={globalFilters.placeholder.isApplied}
            onCheckedChange={globalFilters.placeholder.toggle}
          />
          <label
            htmlFor="placeholder-art"
            className="text-sm font-medium leading-none whitespace-nowrap peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Placeholder Art
          </label>
        </div>
        <Switch
          id="show-previews"
          checked={showPreviews}
          onCheckedChange={(value) => {
            setShowPreviews(value);
            localStorage.setItem("events-show-previews", JSON.stringify(value));
          }}
        />
        <Label
          htmlFor="show-previews"
          className="flex flex-row gap-1 items-center"
        >
          Show Previews
        </Label>
        <GroupsOnlySwitch checked={false} />
      </DataTableActionBar>
      <DataTable table={table} hovering={hovering} />
      {showPreviews && hovering.cell?.column.id === "name" && (
        <RowPreview data={hovering.row?.original.document || null} />
      )}
    </ListView>
  );
};
